import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  Building2,
  Check,
  CreditCard,
  Eye,
  EyeOff,
  KeyRound,
  Loader2,
  Receipt,
  ShieldCheck,
  Zap,
} from "lucide-react";
import { api } from "@/lib/api";

function Field({ label, value, onChange, type = "text", testId, placeholder }) {
  return (
    <div>
      <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">
        {label}
      </label>
      <input
        type={type}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testId}
        className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100 placeholder:text-slate-600 focus:border-amber-500 focus:outline-none"
      />
    </div>
  );
}

function SecretInput({ label, value, onChange, hasConfigured, testId, placeholder, t }) {
  const [visible, setVisible] = useState(false);
  return (
    <div>
      <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">
        {label}
      </label>
      <div className="mt-1 flex gap-2">
        <div className="relative flex-1">
          <input
            type={visible ? "text" : "password"}
            value={value ?? ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder || (hasConfigured ? "•••••••• " + t("adminPlatform.stripe.leaveBlank") : t("adminPlatform.stripe.leaveBlank"))}
            data-testid={testId}
            className="w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 pe-9 text-sm text-slate-100 placeholder:text-slate-600 focus:border-amber-500 focus:outline-none"
          />
          <button
            type="button"
            onClick={() => setVisible((v) => !v)}
            className="absolute end-2 top-1/2 -translate-y-1/2 rounded p-1 text-slate-500 hover:text-slate-200"
            data-testid={`${testId}-toggle`}
            aria-label="show"
          >
            {visible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        </div>
        <span
          className={[
            "inline-flex items-center gap-1 rounded-md px-2 text-[11px] font-medium",
            hasConfigured
              ? "bg-emerald-500/15 text-emerald-300 ring-1 ring-emerald-500/30"
              : "bg-slate-800 text-slate-400 ring-1 ring-slate-700",
          ].join(" ")}
          data-testid={`${testId}-status`}
        >
          {hasConfigured ? t("adminPlatform.stripe.configured") : t("adminPlatform.stripe.notSet")}
        </span>
      </div>
    </div>
  );
}

export default function AdminPlatformSettings() {
  const { t } = useTranslation();
  const [settings, setSettings] = useState(null);
  const [vendor, setVendor] = useState({});
  const [receipts, setReceipts] = useState({
    prefix: "RCP",
    from_name: "",
    from_email: "",
    footer_note_en: "",
    footer_note_sv: "",
    footer_note_ar: "",
  });
  const [stripe, setStripe] = useState({
    stripe_enabled: false,
    stripe_test_mode: true,
    stripe_publishable_key: "",
    stripe_secret_key: "",
    stripe_webhook_secret: "",
  });
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/platform/settings");
      setSettings(data);
      setVendor(data.vendor || {});
      setReceipts({
        prefix: data.receipt_settings?.prefix || "RCP",
        from_name: data.receipt_settings?.from_name || "",
        from_email: data.receipt_settings?.from_email || "",
        footer_note_en: data.receipt_settings?.footer_note_en || "",
        footer_note_sv: data.receipt_settings?.footer_note_sv || "",
        footer_note_ar: data.receipt_settings?.footer_note_ar || "",
      });
      setStripe({
        stripe_enabled: !!data.stripe_enabled,
        stripe_test_mode: !!data.stripe_test_mode,
        stripe_publishable_key: data.stripe_publishable_key || "",
        stripe_secret_key: "",
        stripe_webhook_secret: "",
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || "Failed to load platform settings");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function save() {
    setBusy(true);
    try {
      const payload = {
        vendor,
        receipt_settings: receipts,
        stripe_enabled: stripe.stripe_enabled,
        stripe_test_mode: stripe.stripe_test_mode,
        stripe_publishable_key: stripe.stripe_publishable_key || "",
      };
      // Only send secret fields if user typed something new.
      if (stripe.stripe_secret_key.trim()) {
        payload.stripe_secret_key = stripe.stripe_secret_key.trim();
      }
      if (stripe.stripe_webhook_secret.trim()) {
        payload.stripe_webhook_secret = stripe.stripe_webhook_secret.trim();
      }
      const { data } = await api.patch("/platform/settings", payload);
      setSettings(data);
      setStripe((s) => ({ ...s, stripe_secret_key: "", stripe_webhook_secret: "" }));
      toast.success(t("adminPlatform.saved"));
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || t("adminPlatform.saveFailed"));
    } finally {
      setBusy(false);
    }
  }

  async function testConnection() {
    setTesting(true);
    try {
      const { data } = await api.post("/platform/stripe/test");
      if (data.ok) {
        toast.success(t("adminPlatform.testOk", { id: data.account_id || "" }));
      } else {
        toast.error(t("adminPlatform.testFail", { msg: data.message || data.error }));
      }
    } catch (e) {
      toast.error(t("adminPlatform.testFail", { msg: e?.message || "unknown" }));
    } finally {
      setTesting(false);
    }
  }

  if (loading || !settings) {
    return (
      <div className="flex items-center gap-2 text-slate-400" data-testid="admin-platform-loading">
        <Loader2 className="h-4 w-4 animate-spin" /> …
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="admin-platform-page">
      <header className="flex items-start gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-amber-500/15 text-amber-300 ring-1 ring-inset ring-amber-500/30">
          <ShieldCheck className="h-5 w-5" />
        </div>
        <div>
          <h1 className="text-3xl font-semibold tracking-tight text-white sm:text-4xl">
            {t("adminPlatform.title")}
          </h1>
          <p className="mt-1 text-sm text-slate-400">{t("adminPlatform.subtitle")}</p>
        </div>
      </header>

      {/* Vendor profile */}
      <section
        className="rounded-2xl border border-slate-800 bg-slate-900/70 p-6"
        data-testid="admin-platform-vendor"
      >
        <div className="mb-5 flex items-center gap-2">
          <Building2 className="h-4 w-4 text-amber-400" />
          <h2 className="text-sm font-semibold text-slate-100">{t("adminPlatform.vendorSection")}</h2>
        </div>
        <p className="mb-4 text-xs text-slate-500">{t("adminPlatform.vendorHint")}</p>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={t("adminPlatform.vendor.companyName")} value={vendor.company_name} onChange={(v) => setVendor({ ...vendor, company_name: v })} testId="vendor-company-name" />
          <Field label={t("adminPlatform.vendor.legalName")} value={vendor.legal_name} onChange={(v) => setVendor({ ...vendor, legal_name: v })} testId="vendor-legal-name" />
          <Field label={t("adminPlatform.vendor.orgNr")} value={vendor.org_nr} onChange={(v) => setVendor({ ...vendor, org_nr: v })} testId="vendor-org-nr" />
          <Field label={t("adminPlatform.vendor.vatRegNo")} value={vendor.vat_reg_no} onChange={(v) => setVendor({ ...vendor, vat_reg_no: v })} testId="vendor-vat-reg" />
          <Field label={t("adminPlatform.vendor.email")} value={vendor.email} onChange={(v) => setVendor({ ...vendor, email: v })} testId="vendor-email" type="email" />
          <Field label={t("adminPlatform.vendor.phone")} value={vendor.phone} onChange={(v) => setVendor({ ...vendor, phone: v })} testId="vendor-phone" />
          <div className="sm:col-span-2">
            <Field label={t("adminPlatform.vendor.address")} value={vendor.address} onChange={(v) => setVendor({ ...vendor, address: v })} testId="vendor-address" />
          </div>
          <Field label={t("adminPlatform.vendor.website")} value={vendor.website} onChange={(v) => setVendor({ ...vendor, website: v })} testId="vendor-website" />
          <Field label={t("adminPlatform.vendor.logoUrl")} value={vendor.logo_url} onChange={(v) => setVendor({ ...vendor, logo_url: v })} testId="vendor-logo-url" />
        </div>
      </section>

      {/* Receipt settings — used when generating subscription-payment receipts */}
      <section
        className="rounded-2xl border border-slate-800 bg-slate-900/70 p-6"
        data-testid="admin-platform-receipts"
      >
        <div className="mb-5 flex items-center gap-2">
          <Receipt className="h-4 w-4 text-amber-400" />
          <h2 className="text-sm font-semibold text-slate-100">Subscription receipt settings</h2>
        </div>
        <p className="mb-4 text-xs text-slate-500">
          Applied to receipts sent after each successful tenant Stripe Checkout. Prefix + tri-language footer notes.
        </p>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Receipt number prefix" value={receipts.prefix} onChange={(v) => setReceipts({ ...receipts, prefix: v })} testId="receipt-prefix" placeholder="RCP" />
          <Field label="From name" value={receipts.from_name} onChange={(v) => setReceipts({ ...receipts, from_name: v })} testId="receipt-from-name" />
          <div className="sm:col-span-2">
            <Field label="From email" value={receipts.from_email} onChange={(v) => setReceipts({ ...receipts, from_email: v })} testId="receipt-from-email" type="email" />
          </div>
          <div className="sm:col-span-2">
            <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Footer note — English</label>
            <textarea
              rows={2}
              value={receipts.footer_note_en}
              onChange={(e) => setReceipts({ ...receipts, footer_note_en: e.target.value })}
              data-testid="receipt-footer-en"
              className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100 focus:border-amber-500 focus:outline-none"
            />
          </div>
          <div className="sm:col-span-2">
            <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Footer note — Svenska</label>
            <textarea
              rows={2}
              value={receipts.footer_note_sv}
              onChange={(e) => setReceipts({ ...receipts, footer_note_sv: e.target.value })}
              data-testid="receipt-footer-sv"
              className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100 focus:border-amber-500 focus:outline-none"
            />
          </div>
          <div className="sm:col-span-2">
            <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Footer note — العربية</label>
            <textarea
              rows={2}
              dir="rtl"
              value={receipts.footer_note_ar}
              onChange={(e) => setReceipts({ ...receipts, footer_note_ar: e.target.value })}
              data-testid="receipt-footer-ar"
              className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100 focus:border-amber-500 focus:outline-none"
            />
          </div>
        </div>
      </section>

      {/* Stripe credentials */}
      <section
        className="rounded-2xl border border-slate-800 bg-slate-900/70 p-6"
        data-testid="admin-platform-stripe"
      >
        <div className="mb-5 flex items-center gap-2">
          <CreditCard className="h-4 w-4 text-amber-400" />
          <h2 className="text-sm font-semibold text-slate-100">{t("adminPlatform.stripeSection")}</h2>
        </div>
        <p className="mb-4 text-xs text-slate-500">{t("adminPlatform.stripeHint")}</p>

        {!settings.has_webhook_configured && (
          <div className="mb-4 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-200" data-testid="stripe-webhook-pending">
            <div className="font-semibold">Webhook secret pending</div>
            Register <code className="rounded bg-slate-950 px-1 py-0.5">POST /api/webhooks/stripe</code> in Stripe dashboard (events: <code>checkout.session.completed</code>, <code>customer.subscription.updated</code>, <code>customer.subscription.deleted</code>) — then paste the <code>whsec_…</code> below.
          </div>
        )}

        <div className="grid gap-4 sm:grid-cols-2">
          <label className="flex items-center gap-2 rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100">
            <input
              type="checkbox"
              checked={stripe.stripe_enabled}
              onChange={(e) => setStripe({ ...stripe, stripe_enabled: e.target.checked })}
              data-testid="stripe-enabled"
              className="h-4 w-4 accent-amber-500"
            />
            {t("adminPlatform.stripe.enabled")}
          </label>
          <label className="flex items-center gap-2 rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100">
            <input
              type="checkbox"
              checked={stripe.stripe_test_mode}
              onChange={(e) => setStripe({ ...stripe, stripe_test_mode: e.target.checked })}
              data-testid="stripe-test-mode"
              className="h-4 w-4 accent-amber-500"
            />
            {t("adminPlatform.stripe.testMode")}
          </label>
          <div className="sm:col-span-2">
            <Field
              label={t("adminPlatform.stripe.publishableKey")}
              value={stripe.stripe_publishable_key}
              onChange={(v) => setStripe({ ...stripe, stripe_publishable_key: v })}
              testId="stripe-publishable"
              placeholder="pk_test_…"
            />
          </div>
          <div className="sm:col-span-2">
            <SecretInput
              label={t("adminPlatform.stripe.secretKey")}
              value={stripe.stripe_secret_key}
              onChange={(v) => setStripe({ ...stripe, stripe_secret_key: v })}
              hasConfigured={!!settings.has_secret_configured}
              testId="stripe-secret"
              placeholder="sk_test_…"
              t={t}
            />
          </div>
          <div className="sm:col-span-2">
            <SecretInput
              label={t("adminPlatform.stripe.webhookSecret")}
              value={stripe.stripe_webhook_secret}
              onChange={(v) => setStripe({ ...stripe, stripe_webhook_secret: v })}
              hasConfigured={!!settings.has_webhook_configured}
              testId="stripe-webhook"
              placeholder="whsec_…"
              t={t}
            />
          </div>
        </div>
      </section>

      {/* Action bar */}
      <div className="flex flex-wrap items-center justify-end gap-2">
        <button
          onClick={testConnection}
          disabled={testing || !stripe.stripe_enabled}
          data-testid="stripe-test-btn"
          className="inline-flex items-center gap-2 rounded-lg border border-slate-700 bg-slate-900 px-4 py-2 text-sm text-slate-200 hover:border-amber-500/50 hover:text-amber-300 disabled:opacity-40"
        >
          <Zap className="h-4 w-4" /> {t("adminPlatform.stripe.testConnection")}
        </button>
        <button
          onClick={save}
          disabled={busy}
          data-testid="admin-platform-save"
          className="inline-flex items-center gap-2 rounded-lg bg-amber-500 px-4 py-2 text-sm font-semibold text-slate-950 hover:bg-amber-400 disabled:opacity-60"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
          {t("adminPlatform.save")}
        </button>
      </div>
    </div>
  );
}
